import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import {
  Users, Calendar, Wallet, FileText,
  PlayCircle, ArrowRight, ExternalLink,
  CreditCard, BookOpen, Music, CheckCircle, Clock,
  TrendingUp, Sparkles, ChevronRight, Heart, Star,
  Headphones, BookMarked, Gift, Bell, Zap, Crown,
  CircleDollarSign, Activity, Target, Award, Download, Loader2,
  AlertCircle, Moon
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { EventProgramDialog } from '../../components/EventProgramDialog';
import { loadKhassaides, loadMediatheque, EMPTY_MEDIA } from '../../lib/loadData';
import { collection, query, where, orderBy, limit, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { computeStats, monthlyBreakdown, MONTH_LABELS } from '../../lib/contributions';
import { getHijriDate } from '../../lib/hijri';
import { getCoords, fetchTimings, getNextPrayer, formatCountdown } from '../../lib/prayer';
import enseignements from '../../data/enseignements.json';
import { useAudio } from '../../contexts/AudioContext';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1500 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const startValue = 0;
    const endValue = value;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(startValue + (endValue - startValue) * eased));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return <>{count.toLocaleString()}</>;
};

// Quick Action Button Component
const QuickAction = ({ icon: Icon, label, color, onClick, delay }) => (
  <button
    onClick={onClick}
    className="group flex flex-col items-center gap-2 md:gap-3 p-2 md:p-4 rounded-2xl bg-card hover:bg-muted/80 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl ${color} flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
      <Icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
    </div>
    <span className="text-xs md:text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors text-center leading-tight">
      {label}
    </span>
  </button>
);

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, onClick, delay }) => (
  <Card
    onClick={onClick}
    className={`relative overflow-hidden cursor-pointer group transition-all duration-500 hover:shadow-xl hover:shadow-${color}/10 hover:-translate-y-1 border-border/50 hover:border-${color}/30`}
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className={`absolute inset-0 bg-gradient-to-br from-${color}/10 via-transparent to-transparent opacity-60`} />
    <div className={`absolute -right-8 -top-8 w-32 h-32 bg-${color}/10 rounded-full blur-2xl transition-transform duration-500 group-hover:scale-150`} />

    <CardContent className="p-4 md:p-6 relative">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 md:space-y-2">
          <p className="text-xs md:text-sm font-medium text-muted-foreground line-clamp-1">{title}</p>
          <div className="text-2xl md:text-3xl font-bold tracking-tight">{value}</div>
          {subtitle && (
            <div className="flex items-center gap-1 text-[10px] md:text-xs">
              {trend && <TrendingUp className="h-3 w-3 text-green-500" />}
              <span className={trend ? "text-green-600 font-medium" : "text-muted-foreground"}>
                {subtitle}
              </span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}>
          <Icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Overview() {
  const { user, userData } = useAuth();
  const { playTrack, currentTrack } = useAudio();
  const navigate = useNavigate();
  const [recentContributions, setRecentContributions] = useState([]);
  const [loadingContributions, setLoadingContributions] = useState(true);
  const [contributionStats, setContributionStats] = useState({ total: 0, objectiveTotal: 0, thisMonth: 0, paidCotisationThisMonth: 0 });
  const [myContributions, setMyContributions] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [programOpen, setProgramOpen] = useState(false); // dialog « Voir le programme »

  // Pouls communautaire & campagne active (section "Mon espace")
  const [latestPost, setLatestPost] = useState(null);
  const [duaCount, setDuaCount] = useState(0);
  const [activeCampaign, setActiveCampaign] = useState(null); // événement avec collecte en cours
  useEffect(() => {
    const fetchCommunityAndCampaign = async () => {
      try {
        const postsSnap = await getDocs(query(collection(db, 'community_posts'), orderBy('createdAt', 'desc'), limit(1)));
        if (!postsSnap.empty) setLatestPost({ id: postsSnap.docs[0].id, ...postsSnap.docs[0].data() });
      } catch (e) { /* collection vide ou règles non déployées */ }
      try {
        const duasSnap = await getDocs(collection(db, 'duas'));
        setDuaCount(duasSnap.size);
      } catch (e) { /* ignore */ }
      try {
        // Événement avec collecte en cours (à venir en priorité, sinon le plus récent)
        const evSnap = await getDocs(collection(db, 'events'));
        const fundEvents = evSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => e.fundraising?.enabled);
        const nowMs = Date.now();
        const upcoming = fundEvents
          .filter((e) => new Date(e.date).getTime() >= nowMs)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        const recent = [...fundEvents].sort((a, b) => new Date(b.date) - new Date(a.date));
        setActiveCampaign(upcoming[0] || recent[0] || null);
      } catch (e) { /* ignore */ }
    };
    fetchCommunityAndCampaign();
  }, []);

  // Horaires de prière : prochaine prière en direct (rafraîchie chaque minute)
  const [prayerTimings, setPrayerTimings] = useState(null);
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { lat, lon } = await getCoords();
        const t = await fetchTimings(lat, lon);
        if (active) setPrayerTimings(t);
      } catch (e) { /* silencieux : le bandeau prière reste masqué */ }
    })();
    const id = setInterval(() => setClock(Date.now()), 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // Données volumineuses chargées à la demande (hors bundle principal)
  const [khassaidesData, setKhassaidesData] = useState([]);
  const [mediathequeData, setMediathequeData] = useState(EMPTY_MEDIA);
  useEffect(() => {
    loadKhassaides().then(setKhassaidesData).catch(console.error);
    loadMediatheque().then(setMediathequeData).catch(console.error);
  }, []);
  const [featuredTracks, setFeaturedTracks] = useState([]);
  const [progressValue, setProgressValue] = useState(0);
  const [settings, setSettings] = useState({ annualGoal: 100000, monthlyDue: 5000 });
  const memberCardRef = useRef(null);
  const [downloadingCard, setDownloadingCard] = useState(false);

  // Fonction pour télécharger la carte membre
  const downloadMemberCard = async () => {
    if (!memberCardRef.current) return;

    setDownloadingCard(true);
    try {
      const canvas = await html2canvas(memberCardRef.current, {
        scale: 2,
        backgroundColor: null,
        logging: false,
        useCORS: true
      });

      const link = document.createElement('a');
      link.download = `carte-membre-${userData?.fullName?.replace(/\s+/g, '-') || 'dahira'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Carte de membre téléchargée !');
    } catch (error) {
      console.error('Error downloading card:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingCard(false);
    }
  };

  // Fetch settings for annual goal (now with member-specific goal support)
  useEffect(() => {
    const fetchSettingsAndMemberGoal = async () => {
      try {
        // Récupérer les paramètres généraux (objectif par défaut)
        const settingsSnap = await getDoc(doc(db, 'settings', 'general'));
        let defaultGoal = 100000;
        let monthlyDue = 5000;

        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          defaultGoal = data.annualGoal || 100000;
          monthlyDue = data.cotisationAmount || 5000;
        }

        // Vérifier si le membre connecté a un objectif personnalisé (Sass Annuel)
        let userAnnualGoal = defaultGoal;
        if (user) {
          const userSnap = await getDoc(doc(db, 'users', user.uid));
          if (userSnap.exists() && userSnap.data().annualGoal) {
            userAnnualGoal = userSnap.data().annualGoal;
          }
        }

        setSettings({ annualGoal: userAnnualGoal, monthlyDue });
      } catch (error) { console.error('Error fetching settings:', error); }
    };
    fetchSettingsAndMemberGoal();
  }, [user]);

  // Calculer le nombre total de pistes audio
  const totalAudioTracks = mediathequeData?.audio_archives?.reduce((acc, archive) => {
    return acc + (archive.groups?.reduce((groupAcc, group) => {
      return groupAcc + (group.years?.reduce((yearAcc, year) => {
        return yearAcc + (year.tracks?.length || 0);
      }, 0) || 0);
    }, 0) || 0);
  }, 0) || 0;

  const totalPdfs = khassaidesData?.length || 0;

  // Animate progress on mount and when contribution changes
  useEffect(() => {
    const targetPercentage = Math.min((contributionStats.objectiveTotal / (settings.annualGoal || 100000)) * 100, 100);
    const timer = setTimeout(() => {
      setProgressValue(Math.round(targetPercentage));
    }, 500);
    return () => clearTimeout(timer);
  }, [contributionStats.objectiveTotal, settings.annualGoal]);

  // Pistes en vedette : collectées sur TOUTE la médiathèque (robuste)
  // et couvertures en dégradé (aucune image externe → jamais de vide/404).
  useEffect(() => {
    const gradients = [
      'from-violet-500 to-purple-600',
      'from-emerald-500 to-teal-600',
      'from-blue-500 to-cyan-600',
      'from-amber-500 to-orange-600',
      'from-pink-500 to-rose-600',
      'from-indigo-500 to-blue-600'
    ];
    const m = mediathequeData || {};
    const all = [];
    // 1) Archives audio (groupes / années / pistes, + récitants)
    (m.audio_archives || []).forEach(arch => {
      (arch.groups || []).forEach(group => {
        (group.years || []).forEach(year => {
          (year.tracks || []).forEach(t => all.push({ ...t, artist: group.name, album: `${year.event || ''} ${year.year || ''}`.trim() }));
        });
        (group.tracks || []).forEach(t => all.push({ ...t, artist: group.name }));
      });
      (arch.singers || []).forEach(s => (s.tracks || []).forEach(t => all.push({ ...t, artist: s.name })));
    });
    // 2) Sections riches en pistes + exemples d'écoute
    ['coran_archives', 'wolofal_archives', 'kourel_archives', 'radiass_archives', 'xamxam_archives'].forEach(key => {
      (m[key] || []).forEach(item => (item.tracks || []).forEach(t => all.push({ ...t, artist: item.reciter || item.author || item.title || 'Récital' })));
    });
    (m.sample_tracks || []).forEach(t => all.push({ ...t, artist: t.author || 'Médiathèque' }));

    const featured = all
      .filter(t => t && t.url && t.title)
      .slice(0, 6)
      .map((t, i) => ({ ...t, gradient: gradients[i % gradients.length] }));
    setFeaturedTracks(featured);
  }, [mediathequeData]);

  // Fetch recent contributions and next event
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const qByDate = query(
          collection(db, 'contributions'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(15) // marge pour garder 5 transactions après exclusion de la corbeille
        );

        const statsQuery = query(
          collection(db, 'contributions'),
          where('userId', '==', user.uid)
        );

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayIso = today.toISOString();

        const eventQuery = query(
          collection(db, 'events'),
          where('date', '>=', todayIso),
          orderBy('date', 'asc'),
          limit(1)
        );

        const [recentSnapshot, allSnapshot, eventSnapshot] = await Promise.all([
          getDocs(qByDate),
          getDocs(statsQuery),
          getDocs(eventQuery)
        ]);

        // Exclure les cotisations en corbeille (deleted), comme la page Finances.
        const recentData = recentSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(c => !c.deleted)
          .slice(0, 5);
        setRecentContributions(recentData);

        const allData = allSnapshot.docs.map(doc => doc.data()).filter(c => !c.deleted);
        setMyContributions(allData);
        // Calculs centralisés (exclut rejetées et en attente du total)
        const { total, objectiveTotal, thisMonth } = computeStats(allData);

        setContributionStats({ total, objectiveTotal, thisMonth, paidCotisationThisMonth: thisMonth });

        if (!eventSnapshot.empty) {
          setNextEvent({ id: eventSnapshot.docs[0].id, ...eventSnapshot.docs[0].data() });
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoadingContributions(false);
      }
    };

    fetchData();
  }, [user]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return "Bon après-midi";
    return 'Bonsoir';
  };

  const isPremium = contributionStats.paidCotisationThisMonth > 0;

  // Khassaïde du jour (sélection déterministe par jour de l'année)
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const dailyKhassaide = khassaidesData?.length ? khassaidesData[dayOfYear % khassaidesData.length] : null;
  const dailyTeaching = enseignements.length ? enseignements[dayOfYear % enseignements.length] : null;

  // Compte à rebours vers le prochain événement
  const daysToEvent = nextEvent ? Math.max(0, Math.ceil((new Date(nextEvent.date) - new Date()) / 86400000)) : null;
  const isGamou = nextEvent?.type === 'gamou';

  // Date hégirienne + prochaine prière
  const hijriToday = getHijriDate();
  const gregorianToday = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const nextPrayer = prayerTimings ? getNextPrayer(prayerTimings, new Date(clock)) : null;
  const prayerCountdown = nextPrayer ? formatCountdown(nextPrayer.date - new Date(clock)) : null;

  // Mon espace : suivi de la cotisation du mois
  const myMonthly = monthlyBreakdown(myContributions);
  const currentMonthIdx = new Date().getMonth();
  const monthlyDue = settings.monthlyDue || 5000;
  const paidThisMonth = myMonthly[currentMonthIdx] || 0;
  const monthName = new Date().toLocaleDateString('fr-FR', { month: 'long' });
  const cotisStatus = paidThisMonth >= monthlyDue ? 'paid' : paidThisMonth > 0 ? 'partial' : 'due';
  const paidMonthsCount = myMonthly.filter((a) => a >= monthlyDue).length;
  // Campagne active

  const quickActions = [
    { icon: Wallet, label: 'Cotiser', color: 'bg-gradient-to-br from-emerald-500 to-green-600', onClick: () => navigate('/dashboard/contributions') },
    { icon: Music, label: 'Médiathèque', color: 'bg-gradient-to-br from-violet-500 to-purple-600', onClick: () => navigate('/dashboard/mediatheque') },
    { icon: Calendar, label: 'Événements', color: 'bg-gradient-to-br from-blue-500 to-cyan-600', onClick: () => navigate('/dashboard/events') },
    { icon: Gift, label: 'Dons', color: 'bg-gradient-to-br from-rose-500 to-pink-600', onClick: () => navigate('/dashboard/contributions') },
    { icon: BookOpen, label: 'Formation', color: 'bg-gradient-to-br from-indigo-500 to-blue-600', onClick: () => navigate('/dashboard/learning') },
  ];

  return (
    <div className="space-y-6 md:space-y-8 pb-8">
      {/* Hero Section with Greeting */}
      <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-primary/90 via-primary to-primary/80 p-4 md:p-8 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnptLTYgNmgtNnY2aDZ2LTZ6bTYgMHY2aDZ2LTZoLTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
        <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-secondary/20 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="space-y-3 animate-in fade-in slide-in-from-left-5 duration-700">
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm">👋</span>
              <span className="text-white/80 text-sm font-medium">Bienvenue sur votre espace</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold tracking-tight">
              {getGreeting()}, <span className="text-secondary">{userData?.fullName?.split(' ')[0] || user?.displayName?.split(' ')[0] || 'Membre'}</span>
            </h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-white/75 text-xs md:text-sm">
              <span className="inline-flex items-center gap-1.5 font-medium"><Moon className="h-3.5 w-3.5 text-secondary" />{hijriToday}</span>
              <span className="opacity-40">•</span>
              <span className="capitalize">{gregorianToday}</span>
            </div>
            <p className="text-white/70 max-w-md text-sm md:text-base hidden md:block">
              Ravis de vous revoir sur la plateforme du Daara Wakana Haqqan. Consultez vos activités et restez connecté avec la communauté.
            </p>
          </div>

          <div className="flex gap-2 md:gap-3 animate-in fade-in slide-in-from-right-5 duration-700">
            <Button
              onClick={() => navigate('/dashboard/contributions')}
              size="sm"
              className="bg-white text-primary hover:bg-white/90 shadow-lg shadow-black/20 gap-1 md:gap-2 text-xs md:text-sm"
            >
              <Heart className="h-3 w-3 md:h-4 md:w-4" />
              <span className="hidden md:inline">Cotiser maintenant</span>
              <span className="md:hidden">Cotiser</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard/mediatheque')}
              className="border-white/30 text-white hover:bg-white/10 backdrop-blur text-xs md:text-sm"
            >
              <Headphones className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
              <span className="hidden md:inline">Écouter</span>
            </Button>
          </div>
        </div>

        {/* Bandeau prochaine prière */}
        {nextPrayer && (
          <div className="relative z-10 mt-4 md:mt-6 flex items-center justify-between gap-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Moon className="h-5 w-5 text-secondary" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
                  Prochaine prière{nextPrayer.isTomorrow ? ' (demain)' : ''}
                </p>
                <p className="font-bold truncate">
                  {nextPrayer.label} <span className="text-white/60 font-normal">· {nextPrayer.wolof}</span>
                </p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xl md:text-2xl font-black tabular-nums leading-none">{nextPrayer.time}</p>
              <p className="text-white/70 text-xs mt-1">dans {prayerCountdown}</p>
            </div>
          </div>
        )}

        {/* Live indicator */}
        <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 md:px-3 md:py-1.5 rounded-full">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-xs text-white/80">En ligne</span>
        </div>
      </div>

      {/* Bandeau spirituel : Khassaïde du jour + Compte à rebours événement */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Khassaïde du jour */}
        {(dailyTeaching || dailyKhassaide) && (
          <Card className="lg:col-span-2 relative overflow-hidden border-0 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700 text-white shadow-xl">
            <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-teal-300/10 rounded-full blur-3xl" />
            {/* Grand guillemet décoratif */}
            <div className="absolute right-5 top-0 text-white/10 text-[120px] md:text-[170px] leading-none font-serif select-none pointer-events-none">”</div>

            <CardContent className="relative p-5 md:p-7 flex flex-col h-full min-h-[230px]">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-4 w-4 text-emerald-200" />
                <span className="text-emerald-100 text-xs font-bold uppercase tracking-widest">Sagesse du jour</span>
              </div>

              {dailyTeaching && (
                <div className="flex-1 flex flex-col justify-center">
                  <p className="text-lg md:text-2xl font-semibold leading-snug max-w-2xl">
                    « {dailyTeaching.text} »
                  </p>
                  <p className="mt-3 text-emerald-100/80 text-sm font-medium">
                    — Cheikh Ahmadou Bamba (Serigne Touba)
                    {dailyTeaching.source ? ` · ${dailyTeaching.source}` : (dailyTeaching.theme ? ` · ${dailyTeaching.theme}` : '')}
                  </p>
                </div>
              )}

              {/* Pied : Khassaïde du jour + actions */}
              <div className="mt-5 pt-4 border-t border-white/15 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-200/70 font-bold flex items-center gap-1">
                    <BookMarked className="h-3 w-3" /> Khassaïde du jour
                  </p>
                  <p className="text-sm font-semibold truncate max-w-[200px] md:max-w-xs" dir="auto" title={dailyKhassaide?.title}>
                    {dailyKhassaide?.title?.trim() || '—'}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {dailyKhassaide?.pdfUrl && (
                    <Button size="sm" onClick={() => window.open(dailyKhassaide.pdfUrl, '_blank', 'noopener,noreferrer')} className="bg-white text-emerald-700 hover:bg-white/90 gap-1.5">
                      <BookOpen className="h-4 w-4" /> Lire
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => navigate('/dashboard/mediatheque')} className="border-white/30 text-white hover:bg-white/10 gap-1.5">
                    <Headphones className="h-4 w-4" /> Médiathèque
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compte à rebours prochain événement */}
        {nextEvent && (
          <Card className={`relative overflow-hidden border-0 text-white shadow-xl ${isGamou ? 'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-700' : 'bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700'}`}>
            <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
            <CardContent className="relative p-5 md:p-6 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-white/80" />
                <span className="text-white/80 text-xs font-bold uppercase tracking-widest">{isGamou ? 'Gamou à venir' : 'Prochain événement'}</span>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center py-2">
                <p className="text-5xl md:text-6xl font-black leading-none">{daysToEvent}</p>
                <p className="text-white/70 text-sm font-medium mt-1">{daysToEvent <= 1 ? 'jour restant' : 'jours restants'}</p>
              </div>
              <div className="mt-2">
                <p className="font-bold truncate" title={nextEvent.title}>{nextEvent.title}</p>
                <p className="text-white/70 text-xs">{new Date(nextEvent.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => (nextEvent.program?.trim() ? setProgramOpen(true) : navigate('/dashboard/events'))}
                  className="mt-3 w-full border-white/30 text-white hover:bg-white/10"
                >
                  Voir le programme
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Actions Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Zap className="h-5 w-5 text-secondary" />
            Accès rapide
          </h2>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 md:gap-3">
          {quickActions.map((action, i) => (
            <QuickAction key={i} {...action} delay={i * 100} />
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Cotisations"
          value={<><AnimatedCounter value={contributionStats.total} /> <span className="text-lg font-normal text-muted-foreground">CFA</span></>}
          subtitle={`+${contributionStats.thisMonth.toLocaleString()} ce mois`}
          icon={Wallet}
          color="from-emerald-500 to-green-600"
          trend={contributionStats.thisMonth > 0}
          onClick={() => navigate('/dashboard/contributions')}
          delay={0}
        />

        <StatCard
          title="Statut Membre"
          value={
            <div className="flex items-center gap-2">
              {isPremium ? (
                <>
                  <Crown className="h-6 w-6 text-yellow-500" />
                  <span>Premium</span>
                </>
              ) : (
                <>
                  <Users className="h-6 w-6 text-slate-500" />
                  <span>Standard</span>
                </>
              )}
            </div>
          }
          subtitle={isPremium ? "À jour de cotisation" : "Cotisation en attente"}
          icon={isPremium ? Star : AlertCircle}
          color={isPremium ? "from-yellow-500 to-amber-600" : "from-slate-500 to-slate-600"}
          onClick={() => navigate('/dashboard/contributions')}
          delay={100}
        />

        <StatCard
          title="Objectif annuel"
          value={<><AnimatedCounter value={progressValue} /><span className="text-lg font-normal text-muted-foreground">%</span></>}
          subtitle={`${contributionStats.objectiveTotal.toLocaleString()} / ${(settings.annualGoal || 100000).toLocaleString()} F`}
          icon={Target}
          color="from-amber-500 to-orange-600"
          onClick={() => navigate('/dashboard/contributions')}
          delay={200}
        />

        <Card
          onClick={() => navigate('/dashboard/events')}
          className="relative overflow-hidden cursor-pointer group transition-all duration-500 hover:shadow-xl hover:-translate-y-1 border-border/50 hover:border-purple-500/30"
          style={{ animationDelay: '300ms' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-transparent opacity-60" />
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl transition-transform duration-500 group-hover:scale-150" />

          <CardContent className="p-6 relative">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Prochain Événement</p>
                {nextEvent ? (
                  <>
                    <p className="text-xl font-bold tracking-tight truncate max-w-[180px]" title={nextEvent.title}>
                      {nextEvent.title}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-blue-600">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {new Date(nextEvent.date).toLocaleDateString('fr-FR', {
                          weekday: 'short', day: 'numeric', month: 'short'
                        })}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-medium text-muted-foreground">Aucun prévu</p>
                    <p className="text-xs text-muted-foreground">Consultez le calendrier</p>
                  </>
                )}
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                <Calendar className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mon espace : cotisation du mois, communauté, campagne */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          Mon espace
        </h2>
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">

          {/* Ma cotisation du mois */}
          <Card className="relative overflow-hidden border-border/50">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                    <Wallet className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Ma cotisation</p>
                    <p className="font-bold capitalize leading-none">{monthName}</p>
                  </div>
                </div>
                {cotisStatus === 'paid' ? (
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1"><CheckCircle className="h-3 w-3" />À jour</Badge>
                ) : cotisStatus === 'partial' ? (
                  <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><Clock className="h-3 w-3" />Partiel</Badge>
                ) : (
                  <Badge className="bg-red-500/10 text-red-600 border-red-500/20 gap-1"><AlertCircle className="h-3 w-3" />À régler</Badge>
                )}
              </div>

              <div>
                <p className="text-2xl font-black">{paidThisMonth.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">/ {monthlyDue.toLocaleString()} F</span></p>
                <p className="text-xs text-muted-foreground">{paidMonthsCount}/12 mois à jour cette année</p>
              </div>

              {/* Mini grille 12 mois */}
              <div className="flex gap-1">
                {myMonthly.map((amt, i) => {
                  const st = amt >= monthlyDue ? 'paid' : amt > 0 ? 'partial' : (i <= currentMonthIdx ? 'late' : 'up');
                  const c = { paid: 'bg-emerald-500', partial: 'bg-amber-400', late: 'bg-red-300', up: 'bg-slate-200 dark:bg-slate-700' }[st];
                  return <div key={i} title={`${MONTH_LABELS[i]}: ${amt.toLocaleString()} F`} className={`h-6 flex-1 rounded ${c}`} />;
                })}
              </div>

              {cotisStatus !== 'paid' && (
                <Button size="sm" onClick={() => navigate('/dashboard/contributions')} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  <Heart className="h-4 w-4 mr-2" /> Cotiser maintenant
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Pouls communautaire */}
          <Card className="relative overflow-hidden border-border/50 cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate('/dashboard/communaute')}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                    <Heart className="h-4 w-4 text-white" />
                  </div>
                  <p className="font-bold">Communauté</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
              {latestPost ? (
                <div className="rounded-xl bg-muted/50 p-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">{latestPost.authorName || 'Un membre'}</p>
                  <p className="text-sm line-clamp-2">{latestPost.content}</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-2">Soyez le premier à partager une annonce avec la communauté.</p>
              )}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-amber-500" />{duaCount} intention(s) de du'a</span>
              </div>
            </CardContent>
          </Card>

          {/* Collecte d'événement en cours */}
          {activeCampaign ? (
            <Card className="relative overflow-hidden border-0 text-white shadow-xl bg-gradient-to-br from-violet-600 to-fuchsia-700 cursor-pointer" onClick={() => navigate('/dashboard/events')}>
              <div className="absolute -right-10 -top-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <CardContent className="relative p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-white/80" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-white/70">Collecte en cours</span>
                </div>
                <p className="font-bold text-lg leading-tight line-clamp-1">{activeCampaign.title}</p>
                {activeCampaign.date && (
                  <p className="text-xs text-white/80">
                    {new Date(activeCampaign.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
                {activeCampaign.fundraising?.budget ? (
                  <p className="text-sm font-semibold">Objectif : {activeCampaign.fundraising.budget.toLocaleString()} F</p>
                ) : null}
                <Button size="sm" variant="outline" className="w-full border-white/30 text-white hover:bg-white/10">Cotiser pour l'événement</Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="relative overflow-hidden border-border/50 cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate('/dashboard/events')}>
              <CardContent className="p-5 flex flex-col items-center justify-center text-center h-full gap-2 py-8">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <p className="font-bold">Événements &amp; collectes</p>
                <p className="text-xs text-muted-foreground">Aucune collecte en cours pour le moment.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">

        {/* Featured Media Section - Takes 2 columns */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
              <Headphones className="h-5 w-5 text-violet-500" />
              À l'écoute ce mois-ci
            </h2>
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => navigate('/dashboard/mediatheque')}>
              Tout voir <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {featuredTracks.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {featuredTracks.slice(0, 6).map((track, i) => (
              <div
                key={track.url || i}
                className="group relative cursor-pointer animate-in fade-in slide-in-from-bottom-3 duration-500"
                style={{ animationDelay: `${i * 100}ms` }}
                onClick={() => playTrack(track)}
              >
                <div className={`relative overflow-hidden rounded-2xl aspect-square mb-3 shadow-lg shadow-black/10 group-hover:shadow-xl group-hover:shadow-primary/20 transition-all duration-300 bg-gradient-to-br ${track.gradient || 'from-violet-500 to-purple-600'}`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Music className="h-10 w-10 text-white/40 transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-xl transform scale-75 group-hover:scale-100 transition-transform duration-300">
                      <PlayCircle className="h-8 w-8 text-primary fill-primary/20" />
                    </div>
                  </div>
                  {currentTrack?.url === track.url && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-primary text-white px-2 py-1 rounded-full text-xs">
                      <Activity className="h-3 w-3 animate-pulse" />
                      En cours
                    </div>
                  )}
                </div>
                <div className="space-y-1 px-1">
                  <h3 className="font-medium text-sm leading-tight truncate group-hover:text-primary transition-colors" title={track.title}>{track.title}</h3>
                  <p className="text-xs text-muted-foreground truncate">{track.artist}</p>
                </div>
              </div>
            ))}
          </div>
          ) : (
            <Card className="border-2 border-dashed border-border/60">
              <CardContent className="py-16 flex flex-col items-center text-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <Headphones className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-lg">Médiathèque audio</h3>
                <p className="text-sm text-muted-foreground max-w-xs">Khassaïdes, Coran, Kourel, Wolofal… écoutez les ressources spirituelles du Daara.</p>
                <Button onClick={() => navigate('/dashboard/mediatheque')} className="gap-2"><PlayCircle className="h-4 w-4" /> Ouvrir la médiathèque</Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Member Card & Progress */}
        <div className="space-y-6">

          {/* Premium Member Card */}
          <Card ref={memberCardRef} className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0 shadow-2xl rounded-2xl">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiIGN4PSIyMCIgY3k9IjIwIiByPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-50" />
            <div className="absolute top-4 right-4 w-24 h-24 bg-gradient-to-br from-secondary/50 to-amber-500/30 rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-primary/30 to-transparent rounded-full blur-2xl" />

            <CardContent className="relative p-6 space-y-6">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                    <img
                      src="/logo.jpg"
                      alt="Logo Daara Wakana"
                      className="w-10 h-10 object-cover rounded-lg"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Daara Wakana</p>
                    <p className="font-semibold">Carte Membre</p>
                  </div>
                </div>
                {isPremium ? (
                  <Badge className="bg-gradient-to-r from-yellow-500/80 to-amber-500/80 text-white border-0 shadow-lg">
                    <Crown className="h-3 w-3 mr-1" />
                    PREMIUM
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-white/10 text-slate-300 border-0">
                    STANDARD
                  </Badge>
                )}
              </div>

              <div className="space-y-1 pt-4 border-t border-white/10">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Nom du membre</p>
                <h3 className="text-xl font-bold font-mono tracking-tight">
                  {userData?.fullName || user?.displayName || 'Utilisateur'}
                </h3>
                <p className="text-slate-500 text-xs font-mono flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  ID: {user?.uid?.substring(0, 12).toUpperCase()}
                </p>
              </div>

              <div className="flex justify-between items-end pt-4 border-t border-white/10">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Membre depuis</p>
                  <p className="font-mono text-sm">{userData?.createdAt?.toDate?.()?.getFullYear?.() || new Date().getFullYear() - 1}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Expiration</p>
                  <p className="font-mono text-sm">12/26</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Download Button */}
          <Button
            onClick={downloadMemberCard}
            disabled={downloadingCard}
            className="w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white gap-2 shadow-lg"
          >
            {downloadingCard ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Téléchargement...</>
            ) : (
              <><Download className="h-4 w-4" /> Télécharger ma carte</>
            )}
          </Button>

          {/* Contribution Progress */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Target className="h-4 w-4 text-emerald-500" />
                  Objectif annuel
                </CardTitle>
                <Badge variant="secondary" className="font-mono">{progressValue}%</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Progress value={progressValue} className="h-3" />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  <span className="font-semibold text-foreground">{contributionStats.objectiveTotal.toLocaleString()}</span> CFA
                </span>
                <span className="text-muted-foreground">
                  Objectif: <span className="font-semibold text-foreground">{settings.annualGoal?.toLocaleString()}</span> CFA
                </span>
              </div>
              <Button
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-500/20"
                onClick={() => navigate('/dashboard/contributions')}
              >
                <Heart className="h-4 w-4 mr-2" />
                Contribuer maintenant
              </Button>
            </CardContent>
          </Card>

          {/* Next Event Card */}
          {nextEvent && (
            <Card className="relative overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/events')}>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5" />
              <CardContent className="p-5 relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex flex-col items-center justify-center text-white shadow-lg">
                    <span className="text-xs font-medium uppercase">
                      {new Date(nextEvent.date).toLocaleDateString('fr-FR', { month: 'short' })}
                    </span>
                    <span className="text-xl font-bold leading-none">
                      {new Date(nextEvent.date).getDate()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Prochain événement</p>
                    <h4 className="font-semibold truncate">{nextEvent.title}</h4>
                    <p className="text-sm text-muted-foreground truncate">{nextEvent.location || 'Lieu à confirmer'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Recent Contributions Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-500" />
            Dernières transactions
          </h2>
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => navigate('/dashboard/contributions')}>
            Historique complet <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {loadingContributions ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center gap-2 text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  Chargement...
                </div>
              </div>
            ) : recentContributions.length > 0 ? (
              <div className="divide-y divide-border">
                {recentContributions.map((contribution, i) => (
                  <div
                    key={contribution.id}
                    className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors animate-in fade-in slide-in-from-left-3 duration-300"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm
                        ${contribution.type === 'cotisation'
                          ? 'bg-gradient-to-br from-blue-100 to-blue-50 text-blue-600 dark:from-blue-900/30 dark:to-blue-800/20'
                          : 'bg-gradient-to-br from-purple-100 to-purple-50 text-purple-600 dark:from-purple-900/30 dark:to-purple-800/20'
                        }`}
                      >
                        {contribution.type === 'cotisation' ? <Wallet className="h-5 w-5" /> : <Gift className="h-5 w-5" />}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{contribution.type === 'cotisation' ? 'Cotisation Mensuelle' : 'Don / Zakat'}</p>
                        <p className="text-xs text-muted-foreground">
                          {contribution.createdAt?.toDate?.()
                            ? contribution.createdAt.toDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                            : 'Récemment'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm">{contribution.amount?.toLocaleString()} CFA</p>
                      <Badge
                        variant={contribution.status === 'confirmed' ? 'default' : 'secondary'}
                        className={`text-[10px] uppercase ${contribution.status === 'confirmed' ? 'bg-green-500/10 text-green-600 border-green-500/20' : ''}`}
                      >
                        {contribution.status === 'confirmed' ? (
                          <><CheckCircle className="h-3 w-3 mr-1" /> Confirmé</>
                        ) : (
                          <><Clock className="h-3 w-3 mr-1" /> En attente</>
                        )}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <Wallet className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-medium mb-1">Aucune transaction</h3>
                <p className="text-muted-foreground text-sm mb-4">Commencez à contribuer pour voir votre historique ici</p>
                <Button onClick={() => navigate('/dashboard/contributions')}>
                  <Heart className="h-4 w-4 mr-2" />
                  Faire un don
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Programme du prochain événement */}
      <EventProgramDialog
        event={nextEvent}
        open={programOpen}
        onOpenChange={setProgramOpen}
      />
    </div >
  );
}
